import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import Grid from '@material-ui/core/Grid';
import { debounce, get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../../src/i18n';
import { getFieldName, venueFields } from './constants';
import FormikAutocompleteSelect from '../../Fields/FormikAutocompleteSelect';
import { getOption } from '../../../utils/common';
import NoOptionsBox from '../../NoOptionsBox';
import { TP } from '../../../constants/consts';
import VenueModal from '../../../containers/VenueModal';
import AddNewStageModal from '../../AddNewStageModal';
import Button from '../../Button';
import AddNewCountryModal from '../../AddNewCountryModal';
import AddNewCityModal from '../../AddNewCityModal';
import { getDynamicCountriesAction } from '../../../containers/App/actions';
import { selectAggregationsLoading, selectDynamicCountriesOptions } from '../../../containers/App/selectors';

const { venue, stage, city, country } = venueFields;

const VenuesFieldsGroup = ({
  parentFieldName,
  values,
  loadingValue,
  searchVenue,
  venues,
  cities,
  stages,
  setFieldValue,
  searchCities,
  searchStages,
  clearValues,
  hideStage,
  className,
  isStageCreatable,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const countries = useSelector(selectDynamicCountriesOptions());
  const loadingFieldName = useSelector(selectAggregationsLoading());
  const [countryFieldName] = React.useState(getFieldName(parentFieldName, country));
  const [cityFieldName] = React.useState(getFieldName(parentFieldName, city));
  const [venueFieldName] = React.useState(getFieldName(parentFieldName, venue));
  const [stageFieldName] = React.useState(getFieldName(parentFieldName, stage));

  const getCountries = debounce(v => {
    dispatch(getDynamicCountriesAction({ fieldName: countryFieldName, params: { query: v } }));
  }, 300);

  //  COUNTRY
  const [isNewCountryModalOpened, setNewCountryModalState] = React.useState(false);
  const [newCountryName, setNewCountryName] = React.useState('');
  //  CITY
  const [isNewCityModalOpened, setNewCityModalState] = React.useState(false);
  const [newCityName, setNewCityName] = React.useState('');
  const [cityStartSearching, setCitySearching] = React.useState(false);
  //  VENUE
  const [isNewVenueModalOpened, setNewVenueModalState] = React.useState(false);
  const [newVenueName, setNewVenueName] = React.useState('');
  const [venueStartSearching, setVenueSearcing] = React.useState(false);
  // STAGE
  const [isNewStageModalOpened, setNewStageModalState] = React.useState(false);
  const [newStageName, setNewStageName] = React.useState('');
  const [stagesStartSearching, setStageSearcing] = React.useState(false);

  const openNewCountryModal = () => {
    setNewCountryModalState(true);
  };

  const closeAddNewCountryModal = createdCountry => {
    if (createdCountry?.id) {
      setFieldValue(parentFieldName, {
        ...values,
        [country.name]: getOption(createdCountry) || null,
      });
    }
    setNewCountryModalState(false);
    setNewCountryName('');
  };

  const openNewCityModal = () => {
    setNewCityModalState(true);
  };

  const closeAddNewCityModal = createdCity => {
    if (createdCity?.id) {
      setFieldValue(parentFieldName, {
        ...values,
        [country.name]: getOption(createdCity?.country) || null,
        [city.name]: getOption(createdCity) || null,
        [venue.name]: null,
      });
    }
    setNewCityModalState(false);
    setNewCityName('');
  };

  const openNewVenueModal = () => {
    setNewVenueModalState(true);
  };

  const closeAddNewVenueModal = createdVenue => {
    if (createdVenue?.id) {
      setFieldValue(parentFieldName, {
        ...values,
        [country.name]: getOption(createdVenue?.country) || null,
        [city.name]: getOption(createdVenue?.city) || null,
        [venue.name]: getOption(createdVenue),
      });
    }
    setNewVenueModalState(false);
    setNewVenueName('');
  };

  const openNewStageModal = () => {
    setNewStageModalState(true);
  };

  const closeAddNewStageModal = (updatedVenue, createdStage) => {
    if (createdStage?.id) {
      setFieldValue(parentFieldName, {
        ...values,
        [country.name]: getOption(updatedVenue?.country) || null,
        [city.name]: getOption(updatedVenue?.city) || null,
        [venue.name]: getOption(updatedVenue),
        [stage.name]: getOption(createdStage),
      });
    }
    setNewStageModalState(false);
    setNewStageName('');
  };

  React.useEffect(() => {
    setVenueSearcing(false);
  }, [venues]);

  React.useEffect(() => {
    setStageSearcing(false);
  }, [stages]);

  React.useEffect(() => {
    setCitySearching(false);
  }, [cities]);

  const fieldProps = field => {
    const fieldName = getFieldName(parentFieldName, field);
    return {
      instanceId: fieldName,
      name: fieldName,
      label: t(field.label),
      component: FormikAutocompleteSelect,
      isClearable: true,
      preventDefaultOnChange: true,
    };
  };

  const onCountriesChange = v => {
    setNewCountryName(v);
    getCountries(v);
  };

  return (
    <>
      <Grid container spacing={2} className={className}>
        <Grid item lg={3} sm={6} xs={12}>
          {React.createElement(Field, {
            ...fieldProps(country),
            options: countries,
            onChange: countryValue => {
              if (countryValue) {
                setFieldValue(parentFieldName, {
                  ...values,
                  [country.name]: countryValue,
                });
              } else {
                setFieldValue(parentFieldName, {
                  ...values,
                  [country.name]: countryValue,
                  [city.name]: null,
                  [venue.name]: null,
                });
              }
            },
            customComponents: {
              NoOptionsMessage: () => (
                <NoOptionsBox buttonText={`${TP}.FN_ADD_NEW_COUNTRY`} openFormFunc={openNewCountryModal} t={t} />
              ),
            },
            staticOption: <Button onClick={openNewCountryModal}>{t(`${TP}.FN_ADD_NEW_COUNTRY`)}</Button>,
            onInputTextChange: onCountriesChange,
            isLoading: loadingFieldName === countryFieldName,
          })}
        </Grid>
        <Grid item lg={3} sm={6} xs={12}>
          {React.createElement(Field, {
            ...fieldProps(city),
            options: cities,
            isLoading: loadingValue === cityFieldName,
            isDisabled: !get(values, `${country.name}.value`),
            onInputTextChange: v => {
              setCitySearching(true);
              setNewCityName(v);
              searchCities(cityFieldName, v, get(values, `${country.name}.value`));
            },
            onFocus: v => {
              if (!v) {
                setCitySearching(true);
                searchCities(cityFieldName, '', get(values, `${country.name}.value`));
              }
            },
            onChange: cityValue => {
              setFieldValue(parentFieldName, {
                ...values,
                [city.name]: cityValue,
                [venue.name]: null,
              });
              clearValues(city.name);
              clearValues(venue.name);
            },
            startAggregations: cityStartSearching,
            customComponents: {
              NoOptionsMessage: () => (
                <NoOptionsBox buttonText={`${TP}.FN_ADD_NEW_CITY`} openFormFunc={openNewCityModal} t={t} />
              ),
            },
            staticOption: <Button onClick={openNewCityModal}>{t(`${TP}.FN_ADD_NEW_CITY`)}</Button>,
          })}
        </Grid>
        <Grid item lg={3} sm={6} xs={12}>
          {React.createElement(Field, {
            ...fieldProps(venue),
            options: venues,
            isLoading: loadingValue === venueFieldName,
            onInputTextChange: v => {
              setVenueSearcing(true);
              setNewVenueName(v);
              searchVenue(venueFieldName, v, {
                country_id: values[country.name]?.value,
                city_id: values[city.name]?.value,
              });
            },
            onFocus: v => {
              setVenueSearcing(true);
              if (!v) {
                searchVenue(venueFieldName, '', {
                  country_id: values[country.name]?.value,
                  city_id: values[city.name]?.value,
                });
              }
            },
            onChange: venueData => {
              if (venueData) {
                setFieldValue(parentFieldName, {
                  ...values,
                  [stage.name]: '',
                  [venue.name]: venueData,
                  [country.name]: getOption(venueData?.country),
                  [city.name]: getOption(venueData?.city),
                });
              } else {
                setFieldValue(parentFieldName, {
                  ...values,
                  [stage.name]: '',
                  [venue.name]: venueData,
                });
              }
              clearValues(venue.name);
            },
            startAggregations: venueStartSearching,
            customComponents: {
              NoOptionsMessage: () => (
                <NoOptionsBox buttonText={`${TP}.FN_ADD_NEW_VENUE`} openFormFunc={openNewVenueModal} t={t} />
              ),
            },
            staticOption: <Button onClick={openNewVenueModal}>{t(`${TP}.FN_ADD_NEW_VENUE`)}</Button>,
          })}
        </Grid>
        {!hideStage && (
          <Grid item lg={3} sm={6} xs={12}>
            {React.createElement(Field, {
              ...fieldProps(stage),
              options: stages,
              isDisabled: !get(values, `${venue.name}.value`),
              onInputTextChange: v => {
                setStageSearcing(true);
                setNewStageName(v);
                searchStages(stageFieldName, v, get(values, `${venue.name}.value`));
              },
              onFocus: () => {
                setStageSearcing(true);
                searchStages(stageFieldName, '', get(values, `${venue.name}.value`));
              },
              onChange: stageData => {
                setFieldValue(parentFieldName, {
                  ...values,
                  [stage.name]: stageData,
                });
                clearValues(stage.name);
              },
              startAggregations: stagesStartSearching,
              isLoading: loadingValue === stageFieldName,
              customComponents: isStageCreatable
                ? {}
                : {
                    NoOptionsMessage: () => (
                      <NoOptionsBox buttonText={`${TP}.FN_ADD_NEW_STAGE`} openFormFunc={openNewStageModal} t={t} />
                    ),
                  },
              staticOption: isStageCreatable ? (
                false
              ) : (
                <Button onClick={openNewStageModal}>{t(`${TP}.FN_ADD_NEW_STAGE`)}</Button>
              ),
              isCreatable: isStageCreatable,
            })}
          </Grid>
        )}
      </Grid>
      {isNewCountryModalOpened && (
        <AddNewCountryModal onClose={closeAddNewCountryModal} countryValue={newCountryName} />
      )}
      {isNewCityModalOpened && (
        <AddNewCityModal
          countriesOptions={countries}
          onClose={closeAddNewCityModal}
          countryValue={get(values, country.name)}
          cityValue={newCityName}
        />
      )}
      {isNewVenueModalOpened && (
        <VenueModal
          onClose={closeAddNewVenueModal}
          callback={closeAddNewVenueModal}
          incomingData={{
            name: newVenueName,
            country: get(values, country?.name),
            city: get(values, city?.name),
          }}
          isFormValues
        />
      )}
      {isNewStageModalOpened && (
        <AddNewStageModal
          onClose={closeAddNewStageModal}
          incomingData={{
            name: newStageName,
            country: get(values, country?.name),
            city: get(values, city?.name),
            venue: get(values, venue?.name),
          }}
          countriesOptions={countries}
        />
      )}
    </>
  );
};

VenuesFieldsGroup.propTypes = {
  parentFieldName: PropTypes.string,
  loadingValue: PropTypes.string,
  searchVenue: PropTypes.func.isRequired,
  searchStages: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  venues: PropTypes.array,
  cities: PropTypes.array,
  stages: PropTypes.array,
  hideStage: PropTypes.bool,
  className: PropTypes.string,
  isStageCreatable: PropTypes.bool,
};

VenuesFieldsGroup.defaultProps = {
  venues: [],
  cities: [],
  stages: [],
  searchStages: () => {},
  parentFieldName: '',
  loadingValue: '',
  hideStage: false,
  className: '',
  isStageCreatable: false,
};

export default memo(VenuesFieldsGroup);
